import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnswerInput } from 'shared/providers/ProgramProvider';
import Locations, {
  AddressValue,
} from 'npo/pages/ReportBuilder/Components/Location/Locations';
import { parseAddressValue } from 'npo/pages/ReportBuilder/Components/Location/SectionLocation';

interface LocationFieldProps {
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
}

const LocationField: React.FC<LocationFieldProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [addresses, setAddresses] = useState<AddressValue[]>(
    parseAddressValue(value.text ?? undefined),
  );
  // `Locations` mirrors `addresses` into its own useState initializer, so it
  // only reads the prop on mount. Bump this key once when a late-arriving
  // `value.text` first hydrates addresses, forcing a single remount so
  // Locations reseeds. It then stays stable during editing.
  const [seedKey, setSeedKey] = useState(0);

  // Rehydrate from a late-arriving persisted answer. `value.text` isn't always
  // present on first mount (the parent's `input` can be repopulated after the
  // ProgramProvider patches the questions cache), and useState only runs its
  // initializer once. Only fire while local state is still empty so we never
  // clobber in-progress edits.
  useEffect(() => {
    if (!value.text) return;
    if (addresses.length > 0) return;
    const parsed = parseAddressValue(value.text);
    if (parsed.length > 0) {
      setAddresses(parsed);
      setSeedKey(k => k + 1);
    }
  }, [value.text]);

  // Locations calls onChange on every internal state change via useEffect, so
  // keep the callback identity stable to avoid effect loops.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const handleChange = useCallback((next: AddressValue[]) => {
    onChangeRef.current({
      ...valueRef.current,
      text: next.length ? JSON.stringify(next) : '',
    });
  }, []);

  return (
    <div style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
      <Locations key={seedKey} addresses={addresses} onChange={handleChange} />
    </div>
  );
};

export default LocationField;
